// Concierge travel queue (CHANGE-travel): staff quote a trip (all-in cost and the itinerary the member sees; good for
// 48 hours), book it once an admin accepts (charged amount from the card, at most 5% over the quote, with the
// supplier's receipt), or decline with a reason. A booked trip records each supplier refund, credit, or part not
// provided. Risk flags and the company's signed balance sit on each open trip.
// CHANGE-070: a quote past its time says so (nothing expires it on the server, and accept refuses it); booking,
// recording a refund and declining confirm with the amount first.
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { cents, points } from '../../lib'
import { api } from '../../lib'
import { Button, Busy, Err, Fresh, Page, Section, Sources, dayOf, errorText, useKit, useHashScroll } from './kit'
import { CompanyLink, Facts, parseDollars, useStaffAction, when } from './common'

type Flag = { code: string; reason: string }
type Refund = { id: string; form: 'cash' | 'credit' | 'not_provided'; refund_cents: number; points: number; note: string; created_at: string }
type StaffTrip = {
  id: string; company_id: string; company_name: string; company_status: string; requester_email: string; status: string
  origin: string; destination: string; trip_kind: 'flights' | 'hotel' | 'both'; depart_on: string; return_on: string | null
  flexible_dates: boolean; travelers: number; traveler_names: string[]; contact_email: string; contact_phone: string
  notes: string; max_points: number; cents_per_point: number; cost_cents: number | null; quote_points: number | null
  quote_version: number; quote_expires_at: string | null; itinerary: string | null; charged_cents: number | null
  points_taken: number | null; absorbed_cents: number; booking_reference: string | null; end_reason: string | null
  refund_cents: number; refund_points: number; credit_cents: number; created_at: string
  ready_points: number; spendable: number; flags: Flag[]; absorb_limit_cents: number | null
  refunds: Refund[]; files: { id: string; filename: string }[]
}
const KEY = ['staff', 'trips']
const KIND: Record<StaffTrip['trip_kind'], string> = { flights: 'Flights', hotel: 'Hotel', both: 'Flights and hotel' }
const STATE: Record<string, string> = {
  requested: 'Needs a quote', quoted: 'Waiting for an admin', accepted: 'Accepted: book it', booked: 'Booked',
  canceled: 'Canceled by the member', declined: 'Declined', refunded: 'Refunded',
}
const FORM: Record<Refund['form'], string> = { cash: 'Refund', credit: 'Credit', not_provided: 'Not provided' }
const OPEN = ['requested', 'quoted', 'accepted']

/** Upload a receipt file (PDF, JPEG or PNG) to the trip; resolves to its file id. A raw body, so not api(). */
function useReceipt(tripId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (file: File) => {
      const csrf = document.cookie.match(/pr_csrf=([^;]+)/)?.[1] ?? ''
      const r = await fetch(`/api/staff/trips/${tripId}/files`, {
        method: 'POST', body: file, credentials: 'same-origin',
        headers: { 'x-pr-csrf': csrf, 'x-filename': file.name, 'content-type': file.type || 'application/octet-stream' },
      })
      const body = await r.json()
      if (!r.ok) throw new Error(body.message || 'Upload failed')
      return body as { id: string; filename: string }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export function TravelPage() {
  const q = useQuery({ queryKey: KEY, queryFn: () => api<{ rows: StaffTrip[]; read_at?: string }>('GET', '/api/staff/trips'), refetchInterval: 60_000 })
  useHashScroll(!!q.data)
  if (q.isLoading) return <Busy what="Loading trips" />
  if (q.error) return <Page title="Travel"><Err error={q.error} /></Page>
  const rows = q.data!.rows
  const now = q.data!.read_at ? Date.parse(q.data!.read_at) : Date.now()
  const open = rows.filter((t) => OPEN.includes(t.status))
  const closed = rows.filter((t) => !OPEN.includes(t.status))
  return (
    <Page title="Travel" testid="staff-travel"
      sub={<Sources><Fresh at={q.dataUpdatedAt ? new Date(q.dataUpdatedAt) : null} label="Trips" live limit={180} /></Sources>}>
      <Section title="Open" count={open.length}>
        {open.length === 0 ? <p className="st-note">No open trip requests.</p> : (
          <div className="st-ptn-list">{open.map((t) => <OpenTrip key={t.id} t={t} now={now} />)}</div>
        )}
      </Section>
      {closed.length > 0 ? (
        <Section title="Recent" count={closed.length}>
          <div className="st-ptn-list">{closed.map((t) => <ClosedTrip key={t.id} t={t} />)}</div>
        </Section>
      ) : null}
    </Page>
  )
}

function TripFacts({ t }: { t: StaffTrip }) {
  return (
    <Facts items={[
      ['Company', <><CompanyLink id={t.company_id} name={t.company_name} />{t.company_status !== 'active' ? ` (${t.company_status})` : ''}</>],
      ['Asked by', t.requester_email],
      ['Phone', t.contact_phone],
      ['Route', `${t.origin} to ${t.destination}, ${KIND[t.trip_kind].toLowerCase()}`],
      ['Dates', `${dayOf(t.depart_on)}${t.return_on ? ` to ${dayOf(t.return_on)}` : ', one way'}${t.flexible_dates ? ', 2 days either way' : ''}`],
      ['Travelers, names as on ID', t.traveler_names.join(', ')],
      ['Most points', `${points(t.max_points)} (${cents(t.max_points * t.cents_per_point)})`],
      ['Points balance', <span key="b" className={t.spendable < 0 ? 'st-bad' : undefined} data-testid="staff-trip-spendable">{points(t.spendable)}</span>],
      ['Asked', when(t.created_at)],
    ]} />
  )
}

function Flags({ flags }: { flags: Flag[] }) {
  if (flags.length === 0) return null
  return (
    <ul className="st-trip-flags st-warn" data-testid="staff-trip-flags">
      {flags.map((f) => <li key={f.code} data-code={f.code}>{f.reason}</li>)}
    </ul>
  )
}

function ReceiptPicker({ t, value, onChange }: { t: StaffTrip; value: string; onChange: (id: string) => void }) {
  const upload = useReceipt(t.id)
  return (
    <div className="st-stack">
      <label className="st-field">Supplier receipt, PDF, JPEG or PNG
        <input type="file" accept="application/pdf,image/jpeg,image/png" data-testid="staff-trip-receipt"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) upload.mutate(f, { onSuccess: (r) => onChange(r.id) }) }} />
      </label>
      {upload.isPending ? <p className="st-note">Uploading</p> : null}
      {value ? <p className="st-note">Attached: {t.files.find((f) => f.id === value)?.filename ?? 'receipt'}, <a className="st-link" href={`/api/staff/files/${value}`} target="_blank" rel="noreferrer">open</a></p> : null}
      {upload.error ? <Err error={upload.error} /> : null}
    </div>
  )
}

function OpenTrip({ t, now }: { t: StaffTrip; now: number }) {
  const kit = useKit()
  const quote = useStaffAction<{ cost_cents: number; itinerary: string }>((id) => `/api/staff/trips/${id}/quote`, [KEY])
  const book = useStaffAction<{ charged_cents: number; receipt_file_id: string; reference: string; override_reason: string }>(
    (id) => `/api/staff/trips/${id}/book`, [KEY])
  const decline = useStaffAction<{ reason: string }>((id) => `/api/staff/trips/${id}/decline`, [KEY])
  const [cost, setCost] = useState(t.cost_cents !== null ? (t.cost_cents / 100).toFixed(2) : '')
  const [itinerary, setItinerary] = useState(t.itinerary ?? '')
  const [reference, setReference] = useState('')
  const [charged, setCharged] = useState('')
  const [file, setFile] = useState('')
  const [override, setOverride] = useState('')
  const [reason, setReason] = useState('')
  const costCents = parseDollars(cost)
  const pts = costCents !== null && costCents > 0 ? Math.ceil(costCents / t.cents_per_point) : null
  const available = t.ready_points + (t.status === 'accepted' ? t.quote_points ?? 0 : 0)   // a new quote frees the hold
  const chargedCents = parseDollars(charged)
  const limit = t.absorb_limit_cents ?? 0
  const chargedOk = chargedCents !== null && chargedCents > 0 && chargedCents <= limit
  const negative = t.flags.some((f) => f.code === 'negative_balance')
  const expired = t.status === 'quoted' && !!t.quote_expires_at && Date.parse(t.quote_expires_at) < now
  const fail = { onError: (e: unknown) => kit.toast(errorText(e), { bad: true, ms: 6_000 }) }

  const doBook = async () => {
    if (!chargedOk) return
    const ok = await kit.confirm({
      title: `Mark booked for ${cents(chargedCents!)}`, confirm: 'Mark booked',
      body: <Facts items={[
        ['Company', t.company_name], ['Trip', `${t.origin} to ${t.destination}`], ['Charged', cents(chargedCents!)],
        ['Quote', `${cents(t.cost_cents ?? 0)}, ${points(t.quote_points ?? 0)} points held`],
        ...(chargedCents! > (t.cost_cents ?? 0) ? [['ernd pays', cents(chargedCents! - (t.cost_cents ?? 0))] as [string, string]] : []),
      ]} />,
    })
    if (ok.ok) book.mutate({ id: t.id, body: { charged_cents: chargedCents!, receipt_file_id: file, reference, override_reason: override } }, fail)
  }
  const doDecline = async () => {
    const ok = await kit.confirm({
      title: `Decline ${t.destination} for ${t.company_name}`, confirm: 'Decline', danger: true,
      body: t.status === 'accepted' ? `The ${points(t.quote_points ?? 0)} held points go back. Why: ${reason.trim()}` : `Why: ${reason.trim()}`,
    })
    if (ok.ok) decline.mutate({ id: t.id, body: { reason } }, fail)
  }

  return (
    <section id={t.id} className="st-ptn" data-testid="staff-trip" data-id={t.id} data-status={t.status}>
      <header className="st-ptn-head">
        <h2 className="st-section-title">{t.destination}</h2>
        {expired ? <span className="st-bad" data-testid="staff-trip-expired">Quote expired {when(t.quote_expires_at)}: send a new one</span>
          : <span className={t.status === 'accepted' ? 'st-strong' : 'st-muted'}>{STATE[t.status] ?? t.status}</span>}
      </header>
      <Flags flags={t.flags} />
      <div className="st-two">
        <TripFacts t={t} />
        {t.notes ? <p className="st-trip-notes">{t.notes}</p> : <span />}
      </div>
      {t.status === 'quoted' && t.quote_points !== null ? (
        <p className={expired ? 'st-bad' : 'st-note'} data-testid="staff-trip-quote-line">
          Quote {t.quote_version}: {points(t.quote_points)} points ({cents(t.cost_cents ?? 0)}),{' '}
          {expired ? `expired. The member can't accept it.` : `good until ${when(t.quote_expires_at)}.`}
        </p>
      ) : null}
      {OPEN.includes(t.status) ? (
        <div className="st-stack st-trip-form">
          <label className="st-field">{t.status === 'accepted' ? 'New price. Sends a new quote, and the held points go back' : 'Cost as booked: flights, hotels, taxes and fees'}
            <input className="st-input" data-testid="staff-trip-cost" placeholder="$1,250.00" value={cost} onChange={(e) => setCost(e.target.value)} />
          </label>
          {pts !== null ? (
            <p className="st-note" data-testid="staff-trip-points">
              {points(pts)} points{pts > t.max_points ? <span className="st-warn">, more than the member's {points(t.max_points)}</span> : ''}
              {pts > available ? <span className="st-warn">, more than the {points(available)} ready</span> : ''}
            </p>
          ) : null}
          <label className="st-field">Itinerary the member sees
            <textarea className="st-text" data-testid="staff-trip-itinerary" rows={4} value={itinerary} onChange={(e) => setItinerary(e.target.value)} />
          </label>
          <div>
            <Button data-testid="staff-trip-quote" disabled={pts === null || itinerary.trim() === '' || quote.isPending}
              onClick={() => quote.mutate({ id: t.id, body: { cost_cents: costCents!, itinerary } }, fail)}>
              {t.status === 'requested' ? 'Send quote' : 'Send new quote'}
            </Button>
          </div>
        </div>
      ) : null}
      {t.status === 'accepted' ? (
        <div className="st-stack st-trip-form">
          <p>Accepted: {points(t.quote_points ?? 0)} points held for {cents(t.cost_cents ?? 0)}. Book for at most {cents(limit)}, the quote plus 5%.</p>
          <label className="st-field">Charged, from the card statement
            <input className="st-input" data-testid="staff-trip-charged" placeholder={cents(t.cost_cents ?? 0)} value={charged} onChange={(e) => setCharged(e.target.value)} />
          </label>
          {chargedCents !== null && chargedCents > limit ? <p className="st-bad">More than 5% over the quote. Send a new quote instead.</p> : null}
          {chargedOk && chargedCents! < (t.cost_cents ?? 0) ? (
            <p className="st-note">{points(Math.ceil(chargedCents! / t.cents_per_point))} points used. The rest of the hold goes back.</p>
          ) : null}
          {chargedOk && chargedCents! > (t.cost_cents ?? 0) ? (
            <p className="st-note">{cents(chargedCents! - (t.cost_cents ?? 0))} over the quote is ernd's cost. The member's points stay at the quote.</p>
          ) : null}
          <ReceiptPicker t={t} value={file} onChange={setFile} />
          {negative ? (
            <label className="st-field">Why booking is safe although the balance is or was negative
              <input className="st-input" data-testid="staff-trip-override" value={override} onChange={(e) => setOverride(e.target.value)} />
            </label>
          ) : null}
          <div className="st-row">
            <input className="st-input" data-testid="staff-trip-reference" placeholder="Booking reference" value={reference} onChange={(e) => setReference(e.target.value)} />
            <Button tone="primary" data-testid="staff-trip-book"
              disabled={!chargedOk || !file || (negative && override.trim() === '') || book.isPending} onClick={() => void doBook()}>Mark booked</Button>
          </div>
        </div>
      ) : null}
      <div className="st-row">
        <input className="st-input st-pay-note" data-testid="staff-trip-reason" placeholder="Why we can't book it" value={reason} onChange={(e) => setReason(e.target.value)} />
        <Button tone="danger" data-testid="staff-trip-decline" disabled={reason.trim() === '' || decline.isPending} onClick={() => void doDecline()}>Decline</Button>
      </div>
    </section>
  )
}

function ClosedTrip({ t }: { t: StaffTrip }) {
  const kit = useKit()
  const refund = useStaffAction<{ refund_cents: number; note: string; form: Refund['form']; receipt_file_id?: string; credit_expires_on?: string }>(
    (id) => `/api/staff/trips/${id}/refund`, [KEY])
  const [form, setForm] = useState<Refund['form']>('cash')
  const [amount, setAmount] = useState('')
  const [note, setNote] = useState('')
  const [file, setFile] = useState('')
  const [expires, setExpires] = useState('')
  const refundCents = parseDollars(amount)
  const left = (t.charged_cents ?? 0) - t.refund_cents - t.credit_cents
  const ok = refundCents !== null && refundCents > 0 && refundCents <= left && note.trim() !== '' && (form === 'not_provided' || file !== '')
  const doRecord = async () => {
    if (!ok) return
    const yes = await kit.confirm({
      title: `Record ${FORM[form].toLowerCase()} of ${cents(refundCents!)}`, confirm: 'Record',
      body: <>{t.company_name}, {t.destination}. {form === 'credit' ? 'Held as credit for the next trip.' : 'Points go back to the company.'} {note.trim()}</>,
    })
    if (yes.ok) refund.mutate({ id: t.id, body: { refund_cents: refundCents!, note, form, ...(file ? { receipt_file_id: file } : {}), ...(form === 'credit' && expires ? { credit_expires_on: expires } : {}) } },
      { onError: (e) => kit.toast(errorText(e), { bad: true, ms: 6_000 }) })
  }
  return (
    <section id={t.id} className="st-ptn" data-testid="staff-trip-closed" data-id={t.id}>
      <p>
        <span className="st-strong">{t.destination}</span> for <CompanyLink id={t.company_id} name={t.company_name} />, {dayOf(t.depart_on)}. {STATE[t.status] ?? t.status}
        {t.end_reason ? `: ${t.end_reason}` : ''}
        {t.points_taken !== null ? `. ${points(t.points_taken - t.refund_points)} points spent, ${cents(t.charged_cents ?? 0)} charged` : ''}
        {t.absorbed_cents ? `, ${cents(t.absorbed_cents)} of it ernd's` : ''}
        {t.booking_reference ? `. Reference ${t.booking_reference}` : ''}
      </p>
      {t.refunds.map((r) => (
        <p key={r.id} className="st-note">{FORM[r.form]} {cents(r.refund_cents)} on {dayOf(r.created_at)}: {r.form === 'credit' ? 'held for the next trip' : `${points(r.points)} points back`}. {r.note}</p>
      ))}
      {t.status === 'booked' && left > 0 ? (
        <div className="st-stack">
          <div className="st-row">
            <select className="st-select" value={form} onChange={(e) => setForm(e.target.value as Refund['form'])} data-testid="staff-trip-refund-form">
              <option value="cash">Supplier refunded money</option>
              <option value="credit">Supplier gave a credit</option>
              <option value="not_provided">Not provided, traveler not at fault</option>
            </select>
            <input className="st-input" placeholder={`Amount, up to ${cents(left)}`} value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="staff-trip-refund-amount" />
            <input className="st-input" placeholder="Who and why" value={note} onChange={(e) => setNote(e.target.value)} data-testid="staff-trip-refund-note" />
            {form === 'credit' ? <input className="st-input" type="date" value={expires} onChange={(e) => setExpires(e.target.value)} aria-label="Credit expires on" /> : null}
          </div>
          {form !== 'not_provided' ? <ReceiptPicker t={t} value={file} onChange={setFile} /> : null}
          <div>
            <Button data-testid="staff-trip-refund" disabled={!ok || refund.isPending} onClick={() => void doRecord()}>Record</Button>
          </div>
        </div>
      ) : null}
    </section>
  )
}
